import React from 'react';
import { StyleSheet, View } from 'react-native';
import { CardView, rem, Spacer } from 'design-system';
import { HistogramBar } from 'design-system/HistogramBar';
import BorderedSquircle from 'design-system/BorderedSquircle';
import EmojiText from 'design-system/EmojiText';
import Text from 'design-system/Text';
import IconCircle from 'design-system/icons/IconCircle';
import IconEye from 'design-system/icons/IconEye';
import Squircle from 'design-system/Squircle';
import useColors from 'hooks/useColors';
import TouchableBounce from 'utils/packages/TouchableBounce';
import { isWeb } from '../../../constants';
import { CUSTOM_CATEGORY_COLOR_KEYS } from '../utils';
const strings = {
    previewCaption: 'Preview',
    barLabels: ['Jan', 'Feb', 'Mar', 'Apr', 'May'],
};
const BAR_HEIGHTS = [73, 60, 111, 71, 94]; // pairing with labels
const PreviewCard = React.memo(({ category, color }) => (<CardView style={styles.previewCard}>
      <View style={styles.previewHeading}>
        <IconCircle width={44} height={44}>
          <EmojiText fontSize={20}>{category.emoji}</EmojiText>
        </IconCircle>
        <Spacer.W16 />
        <Text Text-16>{category.name}</Text>
      </View>
      <View style={styles.previewBars}>
        {strings.barLabels.slice(0, -1).map((label, index) => (<View key={index} style={styles.previewBar}>
            <HistogramBar color={color} size={'medium'} height={BAR_HEIGHTS[index]} style={styles.opaque}/>
            <Text Text-12 Secondary>
              {label}
            </Text>
          </View>))}
        <View style={styles.previewBar}>
          <HistogramBar color={color} size={'medium'} height={BAR_HEIGHTS[strings.barLabels.length - 1]}/>
          <Text Text-12>{strings.barLabels[strings.barLabels.length - 1]}</Text>
        </View>
      </View>
    </CardView>));
const PickerCard = ({ colorKey, onChange }) => {
    const colors = useColors();
    return (<CardView withPadding>
      <View style={styles.pickerCard}>
        {CUSTOM_CATEGORY_COLOR_KEYS.map((key) => (<TouchableBounce key={key} onPress={() => onChange(key)} style={styles.colorItem}>
            <BorderedSquircle size={40} borderStrokeProps={{
                stroke: colorKey === key ? colors.elements.brand : undefined,
            }}>
              <Squircle width={28} height={28} color={colors.categories[key]}/>
            </BorderedSquircle>
          </TouchableBounce>))}
      </View>
    </CardView>);
};
/**
 * This is a view consisting of a preview and a color picker.
 */
export const CustomCategoryColorPlayground = ({ category, colorKey, onColorKeyChanged, }) => {
    const colors = useColors();
    return (<View style={styles.container}>
      <View style={styles.previewSection}>
        <PreviewCard category={category} color={colors.categories[colorKey ?? 'custom']}/>
        <View style={styles.previewCaption}>
          <IconEye width={20} height={20} colorKey={'secondary'}/>
          <Text Text-14 Secondary>
            {strings.previewCaption}
          </Text>
        </View>
      </View>
      <PickerCard colorKey={colorKey} onChange={onColorKeyChanged}/>
    </View>);
};
const styles = StyleSheet.create({
    container: {
        flex: isWeb ? 0 : 1,
    },
    previewCard: {
        maxWidth: rem(400),
        width: '100%',
    },
    previewHeading: {
        padding: rem(20),
        paddingBottom: rem(16),
        flexDirection: 'row',
        alignItems: 'center',
    },
    previewBars: {
        flexDirection: 'row',
        justifyContent: 'space-between',
        padding: rem(20),
        paddingTop: rem(8),
        paddingHorizontal: isWeb ? rem(40) : rem(20),
    },
    previewBar: {
        justifyContent: 'flex-end',
        alignItems: 'center',
        gap: rem(16),
    },
    opaque: {
        opacity: 0.2,
    },
    pickerCard: {
        flexDirection: 'row',
        flexWrap: 'wrap',
        rowGap: rem(20),
    },
    colorItem: {
        width: `${100 / 6}%`,
        alignItems: 'center',
    },
    previewSection: {
        alignItems: 'center',
        paddingHorizontal: rem(40),
        ...(isWeb
            ? { marginBottom: rem(64) }
            : { flex: 1, justifyContent: 'center' }),
    },
    previewCaption: {
        flexDirection: 'row',
        gap: rem(8),
        alignItems: 'center',
        justifyContent: 'center',
    },
});
